import { useState, useEffect, useRef } from 'react'
import { useComments } from '../../hooks/useComments'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../i18n'
import { Button } from '../ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import {
  MessageSquare,
  Heart,
  Reply,
  Edit,
  Trash2,
  Pin, Send,
  Loader2,
  ChevronDown,
  ChevronUp,
  Filter,
  X
} from 'lucide-react'
import { showErrorToast } from '../../utils/toast'

// Fonction pour générer un avatar par défaut basé sur le nom de l'utilisateur
const getDefaultAvatar = (name) => {
  if (!name) return 'https://ui-avatars.com/api/?name=User&background=141835&color=fff&size=128'
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=141835&color=fff&size=128`
}

export default function CommentsSection({ courseId }) {
  const { isAuthenticated, user } = useAuth()
  const { tObj, language } = useTranslation()
  const {
    loading,
    createComment,
    getComments,
    updateComment,
    deleteComment,
    likeComment,
    unlikeComment,
    togglePinComment,
    getCommentStats
  } = useComments()

  const [comments, setComments] = useState([])
  const [stats, setStats] = useState(null)
  const [newComment, setNewComment] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editContent, setEditContent] = useState('')
  const [replyingTo, setReplyingTo] = useState(null)
  const [replyContent, setReplyContent] = useState('')
  const [expandedReplies, setExpandedReplies] = useState(new Set())
  const [sortBy, setSortBy] = useState('recent')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const loadMoreRef = useRef(null)
  
  // Filtres avancés
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    hasReplies: null, // null, true, false
    isPinned: null,
    isEdited: null,
    userId: '',
    minLikes: ''
  })

  // Charger les commentaires
  const loadComments = async (pageNum = 1, append = false) => {
    if (loadingMore) return // Éviter les appels multiples
    
    try {
      setLoadingMore(true)
      
      // Construire les paramètres de requête avec les filtres
      const queryParams = {
        sortBy,
        page: pageNum,
        limit: 20
      }
      
      // Ajouter les filtres si définis
      if (filters.hasReplies === true) {
        queryParams.hasReplies = true
      } else if (filters.hasReplies === false) {
        queryParams.noReplies = true
      }
      
      if (filters.isPinned === true) {
        queryParams.isPinned = true
      }
      
      if (filters.isEdited === true) {
        queryParams.isEdited = true
      }
      
      if (filters.userId) {
        queryParams.userId = filters.userId
      }
      
      if (filters.minLikes) {
        queryParams.minLikes = parseInt(filters.minLikes)
      }
      
      const data = await getComments(courseId, queryParams)
      
      const newComments = data.data || []
      
      if (append) {
        setComments(prev => [...prev, ...newComments])
      } else {
        setComments(newComments)
      }
      
      setTotalPages(data.totalPages || 1)
      setPage(pageNum)
      
      // Vérifier s'il y a encore des pages à charger
      setHasMore(pageNum < (data.totalPages || 1))
    } catch (error) {
      console.error('Error loading comments:', error)
      setHasMore(false)
    } finally {
      setLoadingMore(false)
    }
  }

  // Charger les statistiques
  const loadStats = async () => {
    try {
      const data = await getCommentStats(courseId)
      setStats(data)
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  useEffect(() => {
    if (courseId) {
      setPage(1)
      setHasMore(true)
      loadComments(1, false)
      loadStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, sortBy, filters])
  
  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      hasReplies: null,
      isPinned: null,
      isEdited: null,
      userId: '',
      minLikes: ''
    })
  }
  
  // Vérifier si des filtres sont actifs
  const hasActiveFilters = filters.hasReplies !== null || 
                          filters.isPinned === true || 
                          filters.isEdited === true || 
                          filters.userId !== '' || 
                          filters.minLikes !== ''

  // Intersection Observer pour le scroll infini
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loadingMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasMore && !loadingMore && page < totalPages) {
          loadComments(page + 1, true)
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px' // Commencer à charger 100px avant d'arriver en bas
      }
    )

    const currentRef = loadMoreRef.current
    observer.observe(currentRef)

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, page, totalPages, sortBy, courseId])

  // Toggle like
  const handleToggleLike = async (comment) => {
    if (!isAuthenticated) {
      showErrorToast({ message: tObj.comments.loginRequired() })
      return
    }

    try {
      if (comment.isLikedByUser) {
        await unlikeComment(comment.id)
      } else {
        await likeComment(comment.id)
      }
      // Recharger les commentaires
      await loadComments(page, false)
    } catch (error) {
      console.error('Error toggling like:', error)
    }
  }

  // Soumettre un nouveau commentaire
  const handleSubmitComment = async () => {
    if (!newComment.trim()) return
    if (!isAuthenticated) {
      showErrorToast({ message: tObj.comments.loginRequired() })
      return
    }

    try {
      await createComment(courseId, newComment.trim())
      setNewComment('')
      await loadComments(1, false)
      await loadStats()
    } catch (error) {
      console.error('Error creating comment:', error)
    }
  }

  // Soumettre une réponse
  const handleSubmitReply = async () => {
    if (!replyContent.trim() || !replyingTo) return
    if (!isAuthenticated) {
      showErrorToast({ message: tObj.comments.loginRequired() })
      return
    }

    try {
      await createComment(courseId, replyContent.trim(), replyingTo.id)
      setReplyContent('')
      setReplyingTo(null)
      await loadComments(page, false)
      await loadStats()
      // Étendre les réponses pour ce commentaire
      setExpandedReplies(prev => new Set([...prev, replyingTo.id]))
    } catch (error) {
      console.error('Error creating reply:', error)
    }
  }

  // Modifier un commentaire
  const handleEditComment = async () => {
    if (!editContent.trim() || !editingId) return

    try {
      await updateComment(editingId, editContent.trim())
      setEditingId(null)
      setEditContent('')
      await loadComments(page, false)
    } catch (error) {
      console.error('Error updating comment:', error)
    }
  }

  // Supprimer un commentaire
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm(tObj.comments.confirmDelete())) return

    try {
      await deleteComment(commentId)
      await loadComments(page, false)
      await loadStats()
    } catch (error) {
      console.error('Error deleting comment:', error)
    }
  }

  // Épingler/Désépingler
  const handleTogglePin = async (commentId) => {
    try {
      await togglePinComment(commentId)
      await loadComments(page, false)
    } catch (error) {
      console.error('Error toggling pin:', error)
    }
  }

  // Charger les réponses d'un commentaire
  const loadReplies = async (commentId) => {
    try {
      const data = await getComments(courseId, {
        parentId: commentId,
        sortBy: 'recent',
        page: 1,
        limit: 100
      })
      return data.data || []
    } catch (error) {
      console.error('Error loading replies:', error)
      return []
    }
  }

  // Toggle réponses
  const toggleReplies = async (commentId) => {
    const newExpanded = new Set(expandedReplies)
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId)
    } else {
      newExpanded.add(commentId)
    }
    setExpandedReplies(newExpanded)
  }

  // Vérifier les permissions
  const canEdit = (comment) => {
    if (!isAuthenticated || !user) return false
    return comment.userId === user.id || 
           user.role === 'ADMIN' || 
           user.role === 'MODERATOR' ||
           user.role === 'INSTRUCTOR'
  }

  const canDelete = (comment) => {
    if (!isAuthenticated || !user) return false
    return comment.userId === user.id || 
           user.role === 'ADMIN' || 
           user.role === 'MODERATOR' ||
           user.role === 'INSTRUCTOR'
  }

  const canPin = () => {
    if (!isAuthenticated || !user) return false
    return user.role === 'ADMIN' || 
           user.role === 'MODERATOR' ||
           user.role === 'INSTRUCTOR'
  }

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffInSeconds = Math.floor((now - date) / 1000)
      
      if (diffInSeconds < 60) {
        return language === 'fr' ? 'À l\'instant' : 'Just now'
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60)
        return language === 'fr' 
          ? `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`
          : `${minutes} minute${minutes > 1 ? 's' : ''} ago`
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600)
        return language === 'fr'
          ? `Il y a ${hours} heure${hours > 1 ? 's' : ''}`
          : `${hours} hour${hours > 1 ? 's' : ''} ago`
      } else if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400)
        return language === 'fr'
          ? `Il y a ${days} jour${days > 1 ? 's' : ''}`
          : `${days} day${days > 1 ? 's' : ''} ago`
      } else {
        return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      }
    } catch {
      return new Date(dateString).toLocaleDateString()
    }
  }

  return (
    <div className="bg-white border border-[#D5DAE3] p-6 sm:p-8">
      <div className="mb-6">
        <div className="flex items-end gap-4 mb-5">
          <div className="flex-1">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">Communauté</span>
            <h2 className="font-display text-2xl sm:text-3xl text-[#203a8a] mt-1 leading-tight">
              {tObj.comments.title()}
              {stats && (
                <span className="text-[#1B2240]/40 italic font-light text-xl ml-2">
                  ({stats.total})
                </span>
              )}
            </h2>
          </div>
          <MessageSquare className="w-6 h-6 text-[#E8C547] flex-shrink-0 mb-1" />
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 sm:flex-none px-3 py-1.5 border border-[#D5DAE3] rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] min-w-0 bg-white"
          >
            <option value="recent">{tObj.comments.sortRecent()}</option>
            <option value="oldest">{tObj.comments.sortOldest()}</option>
            <option value="most_liked">{tObj.comments.sortMostLiked()}</option>
            <option value="relevant">{tObj.comments.sortRelevant()}</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center justify-center gap-2 flex-1 sm:flex-none min-w-0 ${hasActiveFilters ? 'bg-[#203a8a]/10 border-[#203a8a] text-[#203a8a]' : ''}`}
          >
            <Filter className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{tObj.comments.filters()}</span>
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 bg-[#203a8a] text-white text-[10px] uppercase tracking-[0.22em] flex-shrink-0">
                {Object.values(filters).filter(v => v !== null && v !== '').length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Panneau de filtres avancés */}
      {showFilters && (
        <div className="mb-6 p-5 bg-[#FAFBFD] border border-[#D5DAE3]">
          <div className="flex items-center justify-between mb-4">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">{tObj.comments.advancedFilters()}</span>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="text-sm"
                >
                  <X className="w-4 h-4 mr-1" />
                  {tObj.comments.clearFilters()}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Filtre Réponses */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">
                {tObj.comments.filterReplies()}
              </label>
              <select
                value={filters.hasReplies === null ? '' : filters.hasReplies ? 'has' : 'none'}
                onChange={(e) => {
                  const value = e.target.value
                  setFilters(prev => ({
                    ...prev,
                    hasReplies: value === '' ? null : value === 'has'
                  }))
                }}
                className="w-full px-3 py-2 border border-[#D5DAE3] rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] bg-white"
              >
                <option value="">{tObj.comments.all()}</option>
                <option value="has">{tObj.comments.withReplies()}</option>
                <option value="none">{tObj.comments.withoutReplies()}</option>
              </select>
            </div>

            {/* Filtre Épinglés */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">
                {tObj.comments.filterPinned()}
              </label>
              <select
                value={filters.isPinned === null ? '' : filters.isPinned ? 'yes' : 'no'}
                onChange={(e) => {
                  const value = e.target.value
                  setFilters(prev => ({
                    ...prev,
                    isPinned: value === '' ? null : value === 'yes'
                  }))}
                }
                className="w-full px-3 py-2 border border-[#D5DAE3] rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] bg-white"
              >
                <option value="">{tObj.comments.all()}</option>
                <option value="yes">{tObj.comments.pinnedOnly()}</option>
              </select>
            </div>

            {/* Filtre Modifiés */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">
                {tObj.comments.filterEdited()}
              </label>
              <select
                value={filters.isEdited === null ? '' : filters.isEdited ? 'yes' : 'no'}
                onChange={(e) => {
                  const value = e.target.value
                  setFilters(prev => ({
                    ...prev,
                    isEdited: value === '' ? null : value === 'yes'
                  }))}
                }
                className="w-full px-3 py-2 border border-[#D5DAE3] rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] bg-white"
              >
                <option value="">{tObj.comments.all()}</option>
                <option value="yes">{tObj.comments.editedOnly()}</option>
              </select>
            </div>

            {/* Filtre Minimum de likes */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">
                {tObj.comments.minLikes()}
              </label>
              <input
                type="number"
                min="0"
                value={filters.minLikes}
                onChange={(e) => setFilters(prev => ({ ...prev, minLikes: e.target.value }))}
                placeholder={tObj.comments.minLikesPlaceholder()}
                className="w-full px-3 py-2 border border-[#D5DAE3] rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] bg-white"
              />
            </div>

            {/* Filtre Auteur */}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-2">
                {tObj.comments.filterAuthor()}
              </label>
              <input
                type="text"
                value={filters.userId}
                onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                placeholder={tObj.comments.authorIdPlaceholder()}
                className="w-full px-3 py-2 border border-[#D5DAE3] rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] bg-white"
              />
            </div>
          </div>
        </div>
      )}

      {/* Formulaire de nouveau commentaire */}
      {isAuthenticated && (
        <div className="mb-6 pb-6 border-b border-[#D5DAE3]">
          <div className="flex gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={user?.avatar || getDefaultAvatar(user?.name)} />
              <AvatarFallback>
                <img 
                  src={getDefaultAvatar(user?.name)} 
                  alt={user?.name || 'User'} 
                  className="w-full h-full object-cover"
                />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={tObj.comments.writeComment()}
                className="w-full px-4 py-2 border border-[#D5DAE3] rounded-none focus:outline-none focus:ring-2 focus:ring-[#203a8a] resize-none bg-white"
                rows={3}
                maxLength={2000}
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]">
                  {newComment.length}/2000
                </span>
                <Button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim() || loading}
                  className="bg-[#203a8a] hover:bg-[#1a2f6e] text-white text-xs uppercase tracking-[0.22em] rounded-none"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {tObj.comments.post()}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isAuthenticated && (
        <div className="mb-6 p-4 bg-[#FAFBFD] border border-[#D5DAE3]">
          <p className="text-sm text-[#1B2240]/80">
            {tObj.comments.loginToComment()}
          </p>
        </div>
      )}

      {/* Liste des commentaires */}
      {loading && comments.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#203a8a]" />
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-[#D5DAE3] mx-auto mb-4" />
          <p className="text-[#6B7280]">{tObj.comments.noComments()}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              isAuthenticated={isAuthenticated}
              user={user}
              canEdit={canEdit(comment)}
              canDelete={canDelete(comment)}
              canPin={canPin()}
              onLike={() => handleToggleLike(comment)}
              onEdit={() => {
                setEditingId(comment.id)
                setEditContent(comment.content)
              }}
              onDelete={() => handleDeleteComment(comment.id)}
              onPin={() => handleTogglePin(comment.id)}
              onReply={() => setReplyingTo(comment)}
              replyingTo={replyingTo}
              onLoadReplies={loadReplies}
              expandedReplies={expandedReplies}
              onToggleReplies={toggleReplies}
              replyContent={replyingTo?.id === comment.id ? replyContent : ''}
              onReplyContentChange={(value) => setReplyContent(value)}
              onSubmitReply={handleSubmitReply}
              onCancelReply={() => {
                setReplyingTo(null)
                setReplyContent('')
              }}
              editingId={editingId}
              editContent={editContent}
              onEditContentChange={(value) => setEditContent(value)}
              onSaveEdit={handleEditComment}
              onCancelEdit={() => {
                setEditingId(null)
                setEditContent('')
              }}
              formatDate={formatDate}
              tObj={tObj}
              onLikeReply={likeComment}
              onUnlikeReply={unlikeComment}
            />
          ))}
        </div>
      )}

      {/* Infinite Scroll Sentinel & Loading Indicator */}
      {hasMore && (
        <div 
          ref={loadMoreRef}
          className="flex items-center justify-center py-6"
        >
          {loadingMore && (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">
                {language === 'fr' 
                  ? 'Chargement des commentaires...' 
                  : 'Loading comments...'}
              </span>
            </div>
          )}
        </div>
      )}
      
      {!hasMore && comments.length > 0 && (
        <div className="text-center py-6 text-sm text-gray-500">
          {language === 'fr' 
            ? 'Tous les commentaires ont été chargés' 
            : 'All comments have been loaded'}
        </div>
      )}
    </div>
  )
}

// Composant pour un commentaire individuel
function CommentItem({
  comment,
  isAuthenticated,
  canEdit,
  canDelete,
  canPin,
  onLike,
  onEdit,
  onDelete,
  onPin,
  onReply,
  replyingTo,
  onLoadReplies,
  expandedReplies,
  onToggleReplies,
  replyContent,
  onReplyContentChange,
  onSubmitReply,
  onCancelReply,
  editingId,
  editContent,
  onEditContentChange,
  onSaveEdit,
  onCancelEdit,
  formatDate,
  tObj,
  onLikeReply,
  onUnlikeReply
}) {
  const [replies, setReplies] = useState([])
  const [loadingReplies, setLoadingReplies] = useState(false)

  const isExpanded = expandedReplies.has(comment.id)
  const isEditing = editingId === comment.id

  useEffect(() => {
    if (isExpanded && comment.repliesCount > 0 && replies.length === 0) {
      loadReplies()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, comment.repliesCount])

  const loadReplies = async () => {
    setLoadingReplies(true)
    try {
      const data = await onLoadReplies(comment.id)
      setReplies(data)
    } finally {
      setLoadingReplies(false)
    }
  }

  return (
    <div className={`border p-4 transition-colors ${comment.isPinned ? 'bg-[#E8C547]/10 border-[#E8C547]/60' : 'bg-white border-[#D5DAE3] hover:border-[#203a8a]/40'}`}>
      <div className="flex items-start gap-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={comment.user?.avatar || getDefaultAvatar(comment.user?.name)} />
          <AvatarFallback>
            <img 
              src={getDefaultAvatar(comment.user?.name)} 
              alt={comment.user?.name || 'User'} 
              className="w-full h-full object-cover"
            />
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-semibold text-[#203a8a]">{comment.user?.name}</span>
            {comment.user?.role && (
              <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 border border-[#D5DAE3] text-[#6B7280]">
                {comment.user.role}
              </span>
            )}
            {comment.isPinned && (
              <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 bg-[#E8C547] text-[#203a8a] font-semibold inline-flex items-center gap-1">
                <Pin className="w-3 h-3" />
                {tObj.comments.pinned()}
              </span>
            )}
            {comment.isEdited && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]">({tObj.comments.edited()})</span>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]">{formatDate(comment.createdAt)}</span>

          {isEditing ? (
            <div className="mt-3">
              <textarea
                value={editContent}
                onChange={(e) => onEditContentChange(e.target.value)}
                className="w-full px-3 py-2 border border-[#D5DAE3] rounded-none focus:outline-none focus:ring-2 focus:ring-[#203a8a] resize-none bg-white"
                rows={3}
                maxLength={2000}
              />
              <div className="flex items-center gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={onSaveEdit}
                  className="bg-[#203a8a] hover:bg-[#1a2f6e] text-white text-xs uppercase tracking-[0.22em] rounded-none"
                >
                  {tObj.common.save()}
                </Button>
                <Button size="sm" variant="outline" onClick={onCancelEdit} className="text-xs uppercase tracking-[0.22em] rounded-none">
                  {tObj.common.cancel()}
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-[#1B2240]/80 whitespace-pre-wrap font-sans">{comment.content}</p>
          )}

          {/* Actions */}
          {!isEditing && (
            <div className="flex items-center gap-3 sm:gap-4 mt-3">
              <button
                onClick={onLike}
                disabled={!isAuthenticated}
                className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] transition-colors ${
                  comment.isLikedByUser
                    ? 'text-[#E8C547] font-semibold'
                    : 'text-[#6B7280] hover:text-[#E8C547]'
                }`}
              >
                <Heart className={`w-3.5 h-3.5 ${comment.isLikedByUser ? 'fill-current' : ''}`} />
                {comment.likesCount > 0 && <span>{comment.likesCount}</span>}
              </button>
              <button
                onClick={() => onReply()}
                disabled={!isAuthenticated}
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#6B7280] hover:text-[#203a8a] transition-colors"
              >
                <Reply className="w-3.5 h-3.5" />
                {tObj.comments.reply()}
              </button>
              {comment.repliesCount > 0 && (
                <button
                  onClick={() => onToggleReplies(comment.id)}
                  className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#6B7280] hover:text-[#203a8a] transition-colors"
                >
                  {isExpanded ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                  {comment.repliesCount} {tObj.comments.replies()}
                </button>
              )}
              {canEdit && (
                <button
                  onClick={onEdit}
                  className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280] hover:text-[#203a8a] transition-colors"
                >
                  <Edit className="w-3.5 h-3.5" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={onDelete}
                  className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280] hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              {canPin && (
                <button
                  onClick={onPin}
                  className={`text-[10px] uppercase tracking-[0.22em] transition-colors ${
                    comment.isPinned
                      ? 'text-[#E8C547] hover:text-[#d4af3a]'
                      : 'text-[#6B7280] hover:text-[#E8C547]'
                  }`}
                >
                  <Pin className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}

          {/* Formulaire de réponse */}
          {replyingTo?.id === comment.id && (
            <div className="mt-4 p-3 bg-[#FAFBFD] border border-[#D5DAE3] rounded-none">
              <div className="flex gap-2">
                <textarea
                  value={replyContent}
                  onChange={(e) => onReplyContentChange(e.target.value)}
                  placeholder={tObj.comments.writeReply()}
                  className="flex-1 px-3 py-2 border border-[#D5DAE3] rounded-none focus:outline-none focus:ring-2 focus:ring-[#203a8a] resize-none bg-white"
                  rows={2}
                  maxLength={2000}
                />
                <div className="flex flex-col gap-2">
                  <Button
                    size="sm"
                    onClick={onSubmitReply}
                    disabled={!replyContent.trim()}
                    className="bg-[#203a8a] hover:bg-[#1a2f6e] text-white rounded-none"
                  >
                    <Send className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onCancelReply}
                    className="rounded-none"
                  >
                    {tObj.common.cancel()}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Réponses */}
          {isExpanded && (
            <div className="mt-4 space-y-4 border-l-2 border-[#D5DAE3] pl-4">
              {loadingReplies ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-[#203a8a]" />
                </div>
              ) : replies.length > 0 ? (
                replies.map((reply) => (
                  <div key={reply.id} className="bg-[#FAFBFD] border border-[#D5DAE3] rounded-none p-3">
                    <div className="flex items-start gap-2 mb-1">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={reply.user?.avatar || getDefaultAvatar(reply.user?.name)} />
                        <AvatarFallback>
                          <img 
                            src={getDefaultAvatar(reply.user?.name)} 
                            alt={reply.user?.name || 'User'} 
                            className="w-full h-full object-cover"
                          />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-[#203a8a]">{reply.user?.name}</span>
                      {reply.user?.role && (
                        <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 border border-[#D5DAE3] text-[#6B7280]">
                          {reply.user.role}
                        </span>
                      )}
                          {reply.isEdited && (
                            <span className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]">({tObj.comments.edited()})</span>
                          )}
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.22em] text-[#9CA3AF]">{formatDate(reply.createdAt)}</span>
                        <p className="mt-2 text-sm text-[#1B2240]/80 whitespace-pre-wrap font-sans">{reply.content}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        onClick={async () => {
                          if (!isAuthenticated) {
                            showErrorToast({ message: tObj.comments.loginRequired() })
                            return
                          }
                          try {
                            if (reply.isLikedByUser) {
                              await onUnlikeReply(reply.id)
                            } else {
                              await onLikeReply(reply.id)
                            }
                            // Recharger les réponses pour mettre à jour les likes
                            const updatedReplies = await onLoadReplies(comment.id)
                            setReplies(updatedReplies)
                          } catch (error) {
                            console.error('Error toggling like on reply:', error)
                          }
                        }}
                        disabled={!isAuthenticated}
                        className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] transition-colors ${
                          reply.isLikedByUser
                            ? 'text-[#E8C547] font-semibold'
                            : 'text-[#6B7280] hover:text-[#E8C547]'
                        } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <Heart className={`w-3 h-3 ${reply.isLikedByUser ? 'fill-current' : ''}`} />
                        {reply.likesCount > 0 && <span>{reply.likesCount}</span>}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-[#6B7280]">{tObj.comments.noReplies()}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

